/* src/sections/auth/festival/themes/RakshaBandhanTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Royal & Warm Color Palette for Professional look
const COLORS = {
    BG_DARK: '#2A0A0A',    // Deep brownish-red background base
    BG_WARM: '#5D1212',    // Warmer center glow
    GOLD_MAIN: '#FFC107',  // Rich Gold for threads/text
    GOLD_LIGHT: '#FFE082', // Lighter Gold for particles
    RED_ACCENT: '#D32F2F', // Deep Red accent for threads
};

// Raksha Bandhan Canvas (Animation Engine)
function RakshaBandhanCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        console.log('RakshaBandhanTheme Mounted');
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let threads = [];
            const particles = [];
            let textParticles = [];
            let time = 0;

            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // --- 1. Sacred Thread Factory (Abstract waving silk lines) ---
            const createSacredThread = (color, thickness, speed, offset) => {
                const thread = {
                    color,
                    thickness,
                    speed,
                    offset,
                    points: []
                };
                // Init points
                for (let x = 0; x <= canvas.width + 100; x += 50) {
                    thread.points.push({ x, y: canvas.height / 2 });
                }
                return thread;
            };

            const updateSacredThread = (t) => {
                // Calculate wave movement based on time and x position
                for (let i = 0; i < t.points.length; i += 1) {
                    const p = t.points[i];
                    // Combine multiple sine waves for organic, fluid motion
                    const wave1 = Math.sin(time * t.speed + p.x * 0.005 + t.offset) * 50;
                    const wave2 = Math.cos(time * (t.speed * 1.5) + p.x * 0.003) * 30;
                    // Base Y position changes slowly to drift across screen
                    const driftY = Math.sin(time * 0.001 + t.offset) * (canvas.height * 0.3);
                    p.y = (canvas.height / 2) + wave1 + wave2 + driftY;
                }
            };

            const drawSacredThread = (t) => {
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(t.points[0].x, t.points[0].y);
                // Use bezier curves for smooth silk-like appearance
                for (let i = 1; i < t.points.length - 1; i += 1) {
                    const xc = (t.points[i].x + t.points[i + 1].x) / 2;
                    const yc = (t.points[i].y + t.points[i + 1].y) / 2;
                    ctx.quadraticCurveTo(t.points[i].x, t.points[i].y, xc, yc);
                }

                // Glowing Effect
                ctx.shadowBlur = t.thickness * 4;
                ctx.shadowColor = t.color;
                ctx.strokeStyle = t.color;
                ctx.lineWidth = t.thickness;
                ctx.lineCap = 'round';
                ctx.globalAlpha = 0.6; // Semi-transparent
                ctx.stroke();
                ctx.restore();
            };

            // --- 2. Blessing Particles Factory (Slow rising golden dots) ---
            const createBlessingParticle = () => ({
                x: Math.random() * canvas.width,
                y: canvas.height + Math.random() * 100,
                size: Math.random() * 3 + 1,
                speedY: Math.random() * 0.5 + 0.2,
                alpha: Math.random() * 0.5 + 0.2,
                fadeSpeed: Math.random() * 0.002 + 0.001,
            });

            const updateBlessingParticle = (p) => {
                p.y -= p.speedY;
                p.alpha -= p.fadeSpeed;
                // Reset if faded or off screen
                if (p.alpha <= 0 || p.y < -10) {
                    p.y = canvas.height + 50;
                    p.x = Math.random() * canvas.width;
                    p.alpha = Math.random() * 0.5 + 0.3;
                }
            };

            const drawBlessingParticle = (p) => {
                ctx.save();
                ctx.globalAlpha = p.alpha;
                ctx.fillStyle = COLORS.GOLD_LIGHT;
                ctx.shadowBlur = 5;
                ctx.shadowColor = COLORS.GOLD_LIGHT;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            };

            // --- 3. Text Particle Factory (Header Animation) ---
            const createTextParticle = (x, y, char, index) => ({
                x,
                y: y - 60,
                targetY: y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
            });

            const updateTextParticle = (t) => {
                t.time += 1;
                if (t.time > t.delay) {
                    if (t.y < t.targetY) t.y += 8;
                    t.alpha = Math.min(1, t.alpha + 0.1);
                    t.scale = Math.min(1, t.scale + 0.1);
                }
            };

            const drawTextParticle = (t) => {
                if (t.time < t.delay) return;
                ctx.save();
                ctx.globalAlpha = t.alpha;
                ctx.translate(t.x, t.y);
                ctx.scale(t.scale, t.scale);

                // Font styling
                ctx.font = 'bold 50px "serif", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Gold Glow
                ctx.shadowBlur = 15;
                ctx.shadowColor = COLORS.GOLD_MAIN;
                ctx.fillStyle = COLORS.GOLD_MAIN;

                ctx.fillText(t.char, 0, 0);
                // Subtle border for definition
                ctx.strokeStyle = alpha(COLORS.GOLD_LIGHT, 0.5);
                ctx.lineWidth = 0.5;
                ctx.strokeText(t.char, 0, 0);
                ctx.restore();
            };

            // --- Initialization ---
            // Create a few intertwined threads
            threads.push(createSacredThread(COLORS.GOLD_MAIN, 3, 0.01, 0));
            threads.push(createSacredThread(COLORS.RED_ACCENT, 2, 0.012, Math.PI / 4));
            threads.push(createSacredThread(COLORS.GOLD_LIGHT, 1.5, 0.008, Math.PI / 2));
            threads.push(createSacredThread(COLORS.RED_ACCENT, 2.5, 0.011, Math.PI));

            // Create blessing particles
            for (let i = 0; i < 60; i += 1) particles.push(createBlessingParticle());

            // Create Text
            const createText = () => {
                const text = 'HAPPY RAKSHA BANDHAN';
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.15;
                const charSpacing = canvas.width < 768 ? 28 : 40;
                const startX = centerX - (text.length * charSpacing) / 2;
                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const currentSpacing = text[i] === ' ' ? charSpacing / 2 : charSpacing;
                    textParticles.push(createTextParticle(startX + i * currentSpacing, centerY, text[i], i));
                }
            };
            setTimeout(createText, 50);

            // --- Animation Loop ---
            const animate = () => {
                time += 1;
                // Dark red trail effect background
                ctx.fillStyle = `rgba(42, 10, 10, 0.2)`;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw Threads (Background layer)
                threads.forEach(t => { updateSacredThread(t); drawSacredThread(t); });

                // Draw Particles (Mid layer)
                particles.forEach(p => { updateBlessingParticle(p); drawBlessingParticle(p); });

                // Draw Text (Foreground)
                textParticles.forEach(t => { updateTextParticle(t); drawTextParticle(t); });

                animationFrameId = requestAnimationFrame(animate);
            };
            animate();

            const handleResize = () => {
                resizeCanvas();
                threads = []; // Reset and recreate
                threads.push(createSacredThread(COLORS.GOLD_MAIN, 3, 0.01, 0));
                threads.push(createSacredThread(COLORS.RED_ACCENT, 2, 0.012, Math.PI / 4));
                threads.push(createSacredThread(COLORS.GOLD_LIGHT, 1.5, 0.008, Math.PI / 2));
                threads.push(createSacredThread(COLORS.RED_ACCENT, 2.5, 0.011, Math.PI));

                if (textParticles.length > 0) createText();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 2,
            }}
        />
    );
}

// Main Theme Export
export default function RakshaBandhanTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            background: `radial-gradient(circle at center, ${COLORS.BG_WARM} 0%, ${COLORS.BG_DARK} 90%)`,
            overflow: 'hidden',
        }}>
            <RakshaBandhanCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(20,4,4,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.GOLD_MAIN, 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha(COLORS.RED_ACCENT, 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${COLORS.RED_ACCENT}, ${COLORS.GOLD_MAIN}, ${COLORS.RED_ACCENT}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                    animation: 'rakhiGlow 3s ease-in-out infinite',
                },
                '@keyframes rakhiGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.RED_ACCENT}, ${COLORS.GOLD_MAIN}, ${COLORS.RED_ACCENT})`,
                        backgroundSize: '200% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.RED_ACCENT, 0.5)}`,
                    }}>🧯 &nbsp; Happy Raksha Bandhan &nbsp; 🧯</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}><Logo /></Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.RED_ACCENT, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

RakshaBandhanTheme.propTypes = {
    children: PropTypes.node,
};
